import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    createBatchedParamsArrayFromIds,
    creatorbase,
    creatorbaseQueries,
    instagramQueries,
    InstagramUser,
    InstagramUserImage,
    InstagramUserStatsCache,
} from '@round/api';
import { useAllPosts, type AllPostsConfig as Config } from '@round/creatorbase-helpers/api-hooks';
import { isNumber } from 'utility/utility';
import {
    useInstagramUserImageListQueries,
    useInstagramUserListQueries,
    useInstagramUserStatsCacheListQueries,
} from '@round/platforms/instagram';
import uniq from 'lodash/uniq';
import { getProjectDetailsPostsParams } from './getProjectDetailsPostsParams';

export type InstagramPostsTableRow = creatorbase.InstagramPost & {
    userStatsCache?: InstagramUserStatsCache | null;
    user?: InstagramUser | null;
    image?: InstagramUserImage | null;
};

const ACCOUNTS_BATCH_SIZE = 50;

const buildUserBatchParams = (userIds: number[]) =>
    createBatchedParamsArrayFromIds(userIds, (ids) => ({ id: ids.join(',') }), ACCOUNTS_BATCH_SIZE);

const buildUserImageBatchParams = (userIds: number[]) =>
    createBatchedParamsArrayFromIds(userIds, (ids) => ({ ids }), ACCOUNTS_BATCH_SIZE);

const buildUserStatsCacheBatchParams = (userIds: number[]) =>
    createBatchedParamsArrayFromIds(userIds, (ids) => ({ user_id: ids }), ACCOUNTS_BATCH_SIZE);

const getInstagramUserIds = (posts: creatorbase.InstagramPost[]) =>
    uniq(posts.map((post) => post.instagram_details?.account_id).filter(isNumber));

const assembleRows = (
    posts: creatorbase.InstagramPost[],
    usersById: Map<number, InstagramUser>,
    imagesByUserId: Map<number, InstagramUserImage>,
    statsByUserId: Map<number, InstagramUserStatsCache>
): InstagramPostsTableRow[] =>
    posts.map((post) => {
        const accountId = post.instagram_details?.account_id;
        const user = isNumber(accountId) ? usersById.get(accountId) ?? null : null;
        const userStatsCache = isNumber(accountId) ? statsByUserId.get(accountId) ?? null : null;
        const image = isNumber(accountId) ? imagesByUserId.get(accountId) ?? null : null;

        return { ...post, user, userStatsCache, image };
    });

export default function useProjectDetailsInstagramPosts(campaignId: number, config: Config = { enabled: true }) {
    const postsQuery = useAllPosts(getProjectDetailsPostsParams(campaignId), config);
    const posts = postsQuery.data as creatorbase.InstagramPost[];

    const userIds = getInstagramUserIds(posts);

    const usersData = useInstagramUserListQueries(
        buildUserBatchParams(userIds).map((params) => ({
            params,
            config: { enabled: userIds.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(results.flatMap((result) => result.data?.results ?? []).map((user) => [user.id, user])),
                isLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    const userImagesData = useInstagramUserImageListQueries(
        buildUserImageBatchParams(userIds).map((params) => ({
            params,
            config: { enabled: userIds.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(results.flatMap((result) => result.data ?? []).map((image) => [image.user_id, image])),
                isLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    const userStatsCacheData = useInstagramUserStatsCacheListQueries(
        buildUserStatsCacheBatchParams(userIds).map((params) => ({
            params,
            config: { enabled: userIds.length > 0 },
        })),
        {
            combine: (results) => ({
                byId: new Map(
                    results.flatMap((result) => result.data?.results ?? []).map((stats) => [stats.user_id, stats])
                ),
                isLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    return {
        data: assembleRows(posts, usersData.byId, userImagesData.byId, userStatsCacheData.byId),
        isAccountDataLoading: usersData.isLoading || userImagesData.isLoading || userStatsCacheData.isLoading,
        hasError: postsQuery.hasError,
        isLoading: postsQuery.isLoading,
        refresh: postsQuery.refresh,
    };
}

// Imperative twin of the hook above: read-or-fetch a campaign's enriched posts
// straight from the cache. Use it from event handlers (e.g. copy-to-clipboard)
// where a live subscription isn't wanted. Shares params/keys with the hook, so a
// campaign already rendered by the table is a pure cache hit.
export function useEnsureProjectDetailsInstagramPosts() {
    const queryClient = useQueryClient();

    return useCallback(
        async (campaignId: number): Promise<InstagramPostsTableRow[]> => {
            const posts = (await queryClient.ensureQueryData(
                creatorbaseQueries.posts.options.listAll(getProjectDetailsPostsParams(campaignId))
            )) as creatorbase.InstagramPost[];

            const userIds = getInstagramUserIds(posts);

            const [users, images, stats] = await Promise.all([
                Promise.all(
                    buildUserBatchParams(userIds).map((params) =>
                        queryClient.ensureQueryData(instagramQueries.user.options.list(params))
                    )
                ),
                Promise.all(
                    buildUserImageBatchParams(userIds).map((params) =>
                        queryClient.ensureQueryData(instagramQueries.userImage.options.list(params.ids))
                    )
                ),
                Promise.all(
                    buildUserStatsCacheBatchParams(userIds).map((params) =>
                        queryClient.ensureQueryData(instagramQueries.userStatsCache.options.list(params))
                    )
                ),
            ]);

            const usersById = new Map(users.flatMap((result) => result.results).map((user) => [user.id, user]));
            const imagesByUserId = new Map(images.flat().map((image) => [image.user_id, image]));
            const statsByUserId = new Map(
                stats.flatMap((result) => result.results).map((stat) => [stat.user_id, stat])
            );

            return assembleRows(posts, usersById, imagesByUserId, statsByUserId);
        },
        [queryClient]
    );
}
